import { Button, EmptyState } from '@chopchop-se/ui';
import { FileQuestion, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router';

/**
 * The router's error element. Also catches the "stale chunk after deploy" case:
 * index.html is served `no-store` while `/assets/*` is immutable, so a client
 * that kept a tab open across a deploy asks for a hash that no longer exists.
 * A reload fixes it; the loop guard stops a broken build from reloading forever.
 */
const RELOAD_GUARD_KEY = 'wms.chunkReloadAt';
const RELOAD_GUARD_MS = 30_000;

export function RouteError(): ReactNode {
  const { t } = useTranslation('shell');
  const error = useRouteError();

  if (isDynamicImportFailure(error) && shouldReloadForStaleChunk()) {
    window.location.reload();
    return null;
  }

  const notFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <div className="mx-auto max-w-xl px-4 py-20">
      <EmptyState
        icon={notFound ? <FileQuestion /> : <TriangleAlert />}
        title={notFound ? t('errors.notFoundTitle') : t('errors.pageTitle')}
        description={notFound ? t('errors.notFoundDescription') : t('errors.pageDescription')}
        action={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                window.location.reload();
              }}
            >
              {t('errors.reload')}
            </Button>
            <Link
              to="/"
              className="inline-flex items-center rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
            >
              {t('auth.backToStart')}
            </Link>
          </div>
        }
      />
    </div>
  );
}

/** 404 for an unmatched path — rendered as a normal route, not an error. */
export function NotFoundPage(): ReactNode {
  const { t } = useTranslation('shell');
  return (
    <div className="mx-auto max-w-xl py-16">
      <EmptyState
        icon={<FileQuestion />}
        title={t('errors.notFoundTitle')}
        description={t('errors.notFoundDescription')}
        action={
          <Link
            to="/"
            className="inline-flex items-center rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            {t('auth.backToStart')}
          </Link>
        }
      />
    </div>
  );
}

function isDynamicImportFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i.test(
    error.message,
  );
}

function shouldReloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}
